import React, { useEffect, useState } from "react";
import { Button, Form, Col, Row, Offcanvas, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import { DeliveryZoneEntity } from "../dto/deliveryzone/DeliveryZoneEntity";
import { deliveryZoneCreate, deliveryZoneUpdate } from "../controller/DeliveryZoneController";
import { DeliveryZoneMapPicker } from "./DeliveryZoneMapPicker";

interface Props {
  open: boolean;
  zone: DeliveryZoneEntity | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormErrors {
  name?: string;
  map?: string;        // covers centerLat, centerLng, maxRadiusM together
  centerLat?: string;
  centerLng?: string;
  maxRadiusM?: string;
  feeAmount?: string;
  priority?: string;
}

const EMPTY: DeliveryZoneEntity = {
  name: "",
  centerLat: undefined,
  centerLng: undefined,
  maxRadiusM: undefined,
  feeAmount: undefined,
  currency: "MNT",
  etaText: "",
  status: "ACTIVE",
  priority: 1,
};

export const DeliveryZoneEditDrawer: React.FC<Props> = ({ open, zone, onClose, onSaved }) => {
  const isEdit = zone !== null && zone?.id !== undefined;
  const [form, setForm] = useState<DeliveryZoneEntity>(EMPTY);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(zone ? { ...zone } : { ...EMPTY });
      setErrors({});
    }
  }, [open, zone]);

  const set = (field: keyof DeliveryZoneEntity, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear the error for this field on change
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleMapChange = (lat: number, lng: number, radiusM: number) => {
    setForm((prev) => ({ ...prev, centerLat: lat, centerLng: lng, maxRadiusM: radiusM }));
    setErrors((prev) => ({ ...prev, map: undefined, centerLat: undefined, centerLng: undefined, maxRadiusM: undefined }));
  };

  // Returns errors object — empty means valid
  const validate = (): FormErrors => {
    const e: FormErrors = {};

    if (!form.name || !form.name.trim()) {
      e.name = "Нэр оруулна уу";
    }
    if (form.centerLat == null || form.centerLng == null) {
      e.map = "Газрын зураг дээр төвийг тэмдэглэнэ үү";
    }
    if (!form.maxRadiusM || form.maxRadiusM <= 0) {
      e.map = e.map
        ? "Газрын зураг дээр төв болон радиусыг тохируулна уу"
        : "Газрын зураг дээр радиусыг тохируулна уу";
    }
    if (form.centerLat != null && (form.centerLat < -90 || form.centerLat > 90)) {
      e.centerLat = "Өргөрөг -90 ~ 90 байх ёстой";
    }
    if (form.centerLng != null && (form.centerLng < -180 || form.centerLng > 180)) {
      e.centerLng = "Уртраг -180 ~ 180 байх ёстой";
    }
    if (form.feeAmount == null || form.feeAmount <= 0) {
      e.feeAmount = "Төлбөрийн дүн оруулна уу (0-с их)";
    }
    if (form.priority == null || form.priority < 1) {
      e.priority = "Эрэмбэ 1-с их байх ёстой";
    }

    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        const res = await deliveryZoneUpdate(form);
        if (res.status !== 0) throw new Error(res.msg ?? "Алдаа гарлаа");
        toast.success("Амжилттай шинэчлэгдлээ");
      } else {
        const res = await deliveryZoneCreate(form);
        if (res.status !== 0) throw new Error(res.msg ?? "Алдаа гарлаа");
        toast.success("Амжилттай үүслээ");
      }
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? "Хадгалахад алдаа гарлаа");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Offcanvas show={open} onHide={onClose} placement="end" style={{ width: 600 }}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {isEdit ? `Бүс засах — #${zone?.id}` : "Хүргэлтийн бүс үүсгэх"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form noValidate>

          {/* Map picker */}
          <Form.Group className="mb-3">
            <Form.Label>Газрын зураг <span className="text-danger">*</span></Form.Label>
            <DeliveryZoneMapPicker
              centerLat={form.centerLat}
              centerLng={form.centerLng}
              radiusM={form.maxRadiusM}
              onChange={handleMapChange}
            />
            {errors.map && (
              <div className="text-danger mt-1" style={{ fontSize: 13 }}>
                {errors.map}
              </div>
            )}
          </Form.Group>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Нэр <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={form.name ?? ""}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="жш: УБ төв бүс"
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Статус</Form.Label>
                <Form.Select
                  value={form.status ?? "ACTIVE"}
                  onChange={(e) => set("status", e.target.value)}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Төв өргөрөг <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={form.centerLat ?? ""}
                  onChange={(e) => {
                    const parsed = parseFloat(e.target.value);
                    set("centerLat", e.target.value === "" || isNaN(parsed) ? undefined : parsed);
                  }}
                  placeholder="47.8864"
                  isInvalid={!!errors.centerLat}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.centerLat}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Төв уртраг <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={form.centerLng ?? ""}
                  onChange={(e) => {
                    const parsed = parseFloat(e.target.value);
                    set("centerLng", e.target.value === "" || isNaN(parsed) ? undefined : parsed);
                  }}
                  placeholder="106.9057"
                  isInvalid={!!errors.centerLng}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.centerLng}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Радиус (м) <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={form.maxRadiusM ?? ""}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value);
                    set("maxRadiusM", e.target.value === "" || isNaN(parsed) ? undefined : parsed);
                  }}
                  placeholder="5000"
                  isInvalid={!!errors.maxRadiusM}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.maxRadiusM}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Хүргэлтийн төлбөр <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={form.feeAmount ?? ""}
                  onChange={(e) => {
                    const parsed = parseFloat(e.target.value);
                    set("feeAmount", e.target.value === "" || isNaN(parsed) ? undefined : parsed);
                  }}
                  placeholder="5000"
                  isInvalid={!!errors.feeAmount}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.feeAmount}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Валют</Form.Label>
                <Form.Select
                  value={form.currency ?? "MNT"}
                  onChange={(e) => set("currency", e.target.value)}
                >
                  <option value="MNT">MNT</option>
                  <option value="USD">USD</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Хүргэлтийн хугацаа (ETA)</Form.Label>
                <Form.Control
                  type="text"
                  value={form.etaText ?? ""}
                  onChange={(e) => set("etaText", e.target.value)}
                  placeholder="жш: 1-2 цаг"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Эрэмбэ <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  value={form.priority ?? ""}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value);
                    set("priority", e.target.value === "" || isNaN(parsed) ? undefined : parsed);
                  }}
                  isInvalid={!!errors.priority}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.priority}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Болих
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size="sm" /> : isEdit ? "Шинэчлэх" : "Үүсгэх"}
            </Button>
          </div>

        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};